import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { useFormContext, Controller } from 'react-hook-form';
import ButtonComponent from './ButtonMC';
import IconMC from './IconMC';

/**
 * FileUploaderMC component
 *
 * @param {Function} onFileSelect - **Opcional** - Function to handle the selected file
 * @param {Array} acceptedFileTypes - **Opcional** - Array of accepted file types. If empty, all types are accepted. Example: ['image/png', 'application/pdf', 'video/mp4']
 * @param {String} label - **Opcional** - Label of the button
 * @param {Boolean} preview - **Opcional** - Show or hide file preview. Default: false
 * @param {String} buttonVariant - **Opcional** - Button variant. Default: 'PRIMARY'
 * @param {Object} maxSizeConfig - **Opcional** - Object with the maximum size allowed for each file type. Example: { image: 5 * 1024 * 1024 }
 * @param {File} defaultFile - **Opcional** - Default file to show in the preview
 * @param {String} name - **Opcional** - Name of the input
 */
const FileUploaderMC = (props) => {
  const { t } = useTranslation();
  const {
    onFileSelect,
    acceptedFileTypes = [],
    label = t('file_uploader_mc.label'),
    preview = false,
    buttonVariant = 'PRIMARY',
    maxSizeConfig = null,
    name = ""
  } = props;
  const { control, formState: { errors }, trigger } = useFormContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSetDefaultFile, setIsSetDefaultFile] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [invalidFile, setInvalidFile] = useState(null);
  const fileInputRef = useRef(null);

  const revokePreviewUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const isValidFileType = (file) => {
    if (acceptedFileTypes.length === 0) return true;
    return acceptedFileTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      const regex = new RegExp(`^${type.replace('*', '.*')}$`);
      return regex.test(file.type);
    });
  };

  const handleFileChange = (event, field) => {
    const file = event.target?.files[0];

    if (!file) {
      field.onChange(null);
      setSelectedFile(null);
      revokePreviewUrl(previewUrl);
      setPreviewUrl(null);
      setInvalidFile(null);
      return;
    }

    if (!isValidFileType(file)) {
      setInvalidFile(t('file_uploader_mc.error_invalid_type'));
      control.setError(name, { message: t('file_uploader_mc.error_invalid_type') });
      field.onChange(null);
      setSelectedFile(null);
      revokePreviewUrl(previewUrl);
      setPreviewUrl(null);
      return;
    }

    if (maxSizeConfig) {
      let maxSize = null;
      if (['image/jpg', 'image/jpeg', 'image/png'].includes(file.type)) {
        maxSize = maxSizeConfig.image;
      } else if (file.type === 'application/pdf') {
        maxSize = maxSizeConfig.pdf;
      } else if (['video/mp4', 'video/3gpp', 'video/webm'].includes(file.type)) {
        maxSize = maxSizeConfig.video;
      }

      if (maxSize && file.size > maxSize) {
        const maxSizeInMB = (maxSize / (1024 * 1024)).toFixed(2);
        setInvalidFile(t('file_uploader_mc.error_max_size', { maxSize: maxSizeInMB }));
        control.setError(name, { message: t('file_uploader_mc.error_max_size', { maxSize: maxSizeInMB }) });
        setSelectedFile(null);
        field.onChange(null);
        revokePreviewUrl(previewUrl);
        setPreviewUrl(null);
        return;
      }
    }

    field.onChange(file);
    setSelectedFile(file);
    setInvalidFile(null);
    if (errors[name]) {
      trigger(name);
    }

    if (preview) {
      revokePreviewUrl(previewUrl);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  const handleClearFile = (field) => {
    setSelectedFile(null);
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
    setInvalidFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    field.onChange(null);
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  React.useEffect(() => {
    if (props.defaultFile) {
      setSelectedFile(props.defaultFile);
      setIsSetDefaultFile(true);
      if (preview) {
        revokePreviewUrl(previewUrl);
        const url = URL.createObjectURL(props.defaultFile);
        setPreviewUrl(url);
      }
    }
  }, [props.defaultFile, preview]);

  React.useEffect(() => () => revokePreviewUrl(previewUrl), [previewUrl]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="tw-wrapper">
          <input
            type="file"
            ref={fileInputRef}
            accept={acceptedFileTypes.join(',')}
            onChange={(event) => handleFileChange(event, field)}
            className="tw-hidden"
          />
          <ButtonComponent
            text={label}
            onClick={() => fileInputRef.current.click()}
            variant={buttonVariant}
          />
          {selectedFile && preview && (
            <div className="tw-mt-4">
              {selectedFile.type.startsWith('image/') && (
                <div className="tw-w-24 tw-h-24 tw-rounded tw-overflow-hidden tw-relative">
                  <button
                    type="button"
                    className="tw-absolute tw-top-1 tw-right-1 tw-bg-white tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-focus:outline-none"
                    onClick={() => handleClearFile(field)}
                  >
                    <XCircleIcon className="tw-w-5 tw-h-5 tw-text-red-500" />
                  </button>
                  <img
                    src={previewUrl}
                    alt="Vista previa"
                    className="tw-w-full tw-h-full tw-object-cover"
                  />
                </div>
              )}

              {selectedFile.type.startsWith('video/') && (
                <div className="tw-relative">
                  <div className="tw-w-32 tw-h-32 tw-rounded tw-overflow-hidden  tw-relative">
                    <video src={previewUrl} controls className="tw-w-full tw-h-full tw-object-cover" />
                    <button
                      type="button"
                      className="tw-absolute tw-top-1 tw-right-1 tw-bg-white tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-focus:outline-none"
                      onClick={() => handleClearFile(field)}
                    >
                      <XCircleIcon className="tw-w-5 tw-h-5 tw-text-red-500" />
                    </button>
                  </div>
                </div>
              )}

              {!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/') && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                  <IconMC name="file-text-outline" size="base" className="tw-text-gray-500 tw-flex-shrink-0" />
                  <span className="tw-text-sm tw-text-gray-800 tw-break-all">{selectedFile.name}</span>
                  <button
                    type="button"
                    className="tw-flex-shrink-0 tw-text-red-500"
                    onClick={() => handleClearFile(field)}
                  >
                    <XCircleIcon className="tw-w-5 tw-h-5 tw-text-red-500" />
                  </button>
                </div>
              )}
            </div>
          )}
          {invalidFile && <p className="tw-text-red-500 tw-text-sm tw-mt-2">{invalidFile}</p>}
          {errors[name] && <p className="tw-pt-4 tw-text-xs tw-text-red-500">{errors[name].message}</p>}
        </div>
      )}
    />
  );
};

export default FileUploaderMC;
